// Iter 590 — Tugas Bordir Saya: pekerjaan aktif & serah-terima yang perlu tindakan di atas; sisanya dalam tab ringkas.
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Scissors, Play, Check, Send, Inbox, Users } from "lucide-react";
import { API, enc, HO_ST, ST, post, run, TaskLine } from "./bordir_common";
import { confirmShift } from "./bordir_shift_confirm";
import { notify } from "./modal_center";

// Iter 602 — beri tahu Dashboard Performa agar ringkasan pribadi langsung diperbarui.
const saveProgress = async (t, body) => {
  const shift_choice = await confirmShift();  // Iter 604 — konfirmasi bila di luar jadwal; tidak pernah memblokir
  const r = await post(`/bordir-tasks/${enc(t.id)}/progress`, shift_choice ? { ...body, shift_choice } : body);
  const ev = (r?.data?.history || []).filter(h => h.action === "progress" || h.action === "done").slice(-1)[0];
  if (ev?.shift_choice_rejected) notify.warning({ title: "Pilihan shift tidak berlaku lagi",
    message: `Progres tetap tersimpan sebagai Shift ${ev.shift === "siang" ? "Siang" : "Malam"} tanggal kerja ${ev.work_date} (ditentukan otomatis dari jadwal). Hubungi Owner/Admin bila perlu dikoreksi.` });
  window.dispatchEvent(new Event("bordir-progress-saved"));
  return r;
};
import { Stepper } from "./bordir_split";
import "./bordir_tasks.css";

export { BordirTasksPanel, BordirSplitPanel, useBordirReadiness, ReadinessList } from "./bordir_split";

function WorkLeft({ h }) {
  if (!h.work_left?.length) return null;
  return <span className="bt-warn" data-testid={`bordir-work-left-${h.id}`}>Belum selesai: {h.work_left.map(w => `${w.operator} ${w.positions.join(", ")} ${w.pcs_left} pcs`).join("; ")}</span>;
}

function ReceiveRow({ h, reload }) {
  const [pcs, setPcs] = useState(h.pcs_sent);
  const [note, setNote] = useState("");
  const diff = h.pcs_sent - Number(pcs);
  const submit = () => run(() => post(`/bordir-tasks/handover/${enc(h.id)}/receive`, { pcs_received: Number(pcs), note }),
    diff ? "Diterima dengan selisih — menunggu penyelesaian pemegang SPK" : "Barang diterima", reload);
  return (
    <div className="bt-task bt-incoming" data-testid={`bordir-incoming-${h.id}`}>
      <Inbox size={14}/> <b>SPK {h.order_number}</b> · {h.product_name} · {h.from_name} menyerahkan <b>{h.pcs_sent} pcs</b> ({h.bundle_label})
      {!!h.positions_next?.length && <span className="bt-muted">· untuk {h.positions_next.join(", ")}</span>}
      <WorkLeft h={h}/>
      <span className="bt-row"><span>Diterima</span><Stepper value={pcs} min={0} max={h.pcs_sent} label="pcs diterima" testid={`bordir-receive-pcs-${h.id}`} onChange={setPcs}/></span>
      {diff > 0 && <><span className="bt-warn" data-testid={`bordir-receive-diff-${h.id}`}>Selisih {diff} pcs</span>
        <input type="text" placeholder="Catatan selisih/kerusakan" value={note} onChange={e => setNote(e.target.value)} data-testid={`bordir-receive-note-${h.id}`}/></>}
      <button type="button" className="bt-btn bt-ok" disabled={pcs === "" || diff < 0 || (diff > 0 && note.trim().length < 3)} onClick={submit} data-testid={`bordir-receive-${h.id}`}>Terima</button>
    </div>
  );
}

function SendRow({ hd, operators, reload }) {
  const [to, setTo] = useState("");
  const [pcs, setPcs] = useState(hd.pcs);
  const tid = `${hd.order_number}-${hd.product_id}-${hd.bundle_id}`;
  const submit = () => run(() => post("/bordir-tasks/handover", { order_number: hd.order_number, product_id: hd.product_id, bundle_id: hd.bundle_id,
    to_operator_id: to, pcs: Number(pcs) }), "Diserahkan — menunggu konfirmasi penerima", reload);
  return (
    <div className="bt-task" data-accent="work" data-testid={`my-bordir-hold-${tid}`}>
      <span><b>SPK {hd.order_number}</b> · {hd.bundle_label} · dipegang <b>{hd.pcs} pcs</b></span>
      <select value={to} onChange={e => setTo(e.target.value)} data-testid={`my-bordir-handover-to-${tid}`}>
        <option value="">Serahkan ke…</option>{operators.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
      </select>
      <Stepper value={pcs} min={1} max={hd.pcs} label="pcs diserahkan" testid={`my-bordir-handover-pcs-${tid}`} onChange={setPcs}/>
      <button type="button" className="bt-btn" disabled={!to || !(Number(pcs) > 0) || Number(pcs) > hd.pcs} onClick={submit} data-testid={`my-bordir-handover-${tid}`}><Send size={13}/> Serahkan</button>
    </div>
  );
}

function ActiveTask({ t, reload }) {
  const can = t.pcs_can_add ?? 0;
  const max = t.pcs_done + can;
  const [v, setV] = useState(max);
  const bad = !(Number(v) > t.pcs_done && Number(v) <= max);
  const complete = t.pcs_done === t.pcs_assigned;
  return (
    <div className="bt-task" data-accent={t.status === "in_progress" ? "work" : t.status === "done" ? "ready" : "queue"} data-testid={`my-bordir-task-${t.id}`}>
      <TaskLine t={t} showOrder/>{!!t.mates?.length && <span className="bt-muted">· bersama {t.mates.join(", ")}</span>}
      <span className="bt-states" data-testid={`my-bordir-states-${t.id}`}>
        <span>Barang di tangan: <b>{t.pcs_holding ?? 0}</b></span>
        <span>Progres bordir: <b>{t.pcs_done}/{t.pcs_assigned}</b></span>
        {!complete && <span className={can ? "" : "bt-warn"}>Bisa dikerjakan: <b>{can}</b>{can ? "" : " — minta barang diserahkan"}</span>}
      </span>
      {t.status === "assigned" && <button type="button" className="bt-btn bt-ok" onClick={() => run(() => post(`/bordir-tasks/${enc(t.id)}/start`), "Tugas dimulai", reload)}
        data-testid={`my-bordir-start-${t.id}`}><Play size={13}/> Mulai</button>}
      {t.status === "in_progress" && !complete && can > 0 && <span className="bt-ho">
        <span>Total selesai</span><Stepper value={v} min={t.pcs_done + 1} max={max} label="total pcs selesai dibordir" testid={`my-bordir-pcs-${t.id}`} onChange={setV}/>
        <button type="button" className="bt-btn bt-ok" disabled={bad} onClick={() => run(() => saveProgress(t, { pcs_done: Number(v) }), "Progres disimpan", reload)}
          data-testid={`my-bordir-save-${t.id}`}>Simpan Progres</button></span>}
      {t.status === "in_progress" && complete && <button type="button" className="bt-btn bt-ok" data-testid={`my-bordir-finish-${t.id}`}
        onClick={() => run(() => saveProgress(t, { pcs_done: t.pcs_assigned, finish: true }), "Tugas selesai", reload)}>
        <Check size={13}/> Selesaikan Tugas Saya</button>}
    </div>
  );
}

function TeamSummary({ team }) {
  if (!team?.length) return null;
  return (
    <section className="bt-panel" data-testid="my-bordir-team">
      <div className="bt-head"><Users size={16}/><strong>Tim SPK Saya</strong><span className="bt-muted">SPK yang sedang Anda pegang</span></div>
      {team.map(it => (
        <details key={it.order_number + it.product_id} className="bt-team" data-testid={`my-bordir-team-${it.order_number}-${it.product_id}`}>
          <summary>
            <b>SPK {it.order_number}</b> · {it.product_name} · {it.rows.length} tugas ·{" "}
            {it.ready ? <span className="bt-st bt-done">Siap Selesai Tahap</span>
              : <span className="bt-warn">{it.reasons.length} hal belum lengkap</span>}
          </summary>
          {it.rows.map(r => (
            <div key={r.id} className="bt-team-row" data-testid={`my-bordir-team-row-${r.id}`}>
              <b>{r.operator}</b> · {r.bundle} · {r.positions.join(", ")}{r.is_rework ? " · Perbaikan" : ""} ·{" "}
              {r.pcs_done}/{r.pcs_assigned} pcs · <span className={`bt-st bt-${r.status}`}>{ST[r.status]}</span>
            </div>))}
          {it.handovers.map(h => <div key={h.id} className="bt-team-row bt-warn">Serah-terima {h.text}: {HO_ST[h.status]}</div>)}
          {!it.ready && <ul className="bt-team-reasons">{it.reasons.map((x, i) => <li key={i}>{x}</li>)}</ul>}
        </details>))}
    </section>
  );
}

function TransferRow({ it, reload }) {
  const base = `/bordir-tasks/transfer/${enc(it.order_number)}/${enc(it.product_id)}`;
  return (
    <div className="bt-task bt-incoming" data-testid={`my-bordir-transfer-${it.order_number}-${it.product_id}`}>
      <Inbox size={14}/> {it.transfer.from_name} mengalihkan penanggung jawab <b>SPK {it.order_number}</b> kepada Anda · "{it.transfer.note}"
      <button type="button" className="bt-btn bt-ok" onClick={() => run(() => post(`${base}/accept`), "Anda kini pemegang SPK", reload)} data-testid={`my-bordir-transfer-accept-${it.order_number}`}>Terima</button>
      <button type="button" className="bt-link" onClick={() => run(() => post(`${base}/reject`), "Pengajuan ditolak", reload)} data-testid={`my-bordir-transfer-reject-${it.order_number}`}>Tolak</button>
    </div>
  );
}

export function MyBordirTasks() {
  const [d, setD] = useState(null);
  const [tab, setTab] = useState("aktif");
  const load = useCallback(async () => {
    try { setD((await axios.get(`${API}/bordir-tasks/mine`, { withCredentials: true })).data); } catch { /* panel opsional */ }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    window.addEventListener("bordir-op-tasks-changed", load);
    window.addEventListener("bordir-op-queue-changed", load);
    return () => { window.removeEventListener("bordir-op-tasks-changed", load); window.removeEventListener("bordir-op-queue-changed", load); };
  }, [load]);
  if (!d) return null;
  const nHo = d.holdings.length + d.outgoing_handovers.length;
  const team = <TeamSummary team={d.team}/>;
  if (!d.tasks.length && !d.done_tasks.length && !d.incoming_handovers.length && !d.incoming_transfers.length && !nHo) return team;
  const tabs = [["aktif", "Sedang Dikerjakan", d.tasks.length], ["serah", "Serah-terima", nHo], ["selesai", "Selesai", d.done_tasks.length]];
  return (<>
    {team}
    <section className="bt-panel" data-testid="my-bordir-tasks">
      <div className="bt-head"><Scissors size={16}/><strong>Tugas Bordir Saya</strong></div>
      {d.incoming_transfers.map(it => <TransferRow key={it.order_number + it.product_id} it={it} reload={load}/>)}
      {d.incoming_handovers.map(h => <ReceiveRow key={h.id} h={h} reload={load}/>)}
      <nav className="bt-tabs" role="tablist">{tabs.map(([k, label, n]) => (
        <button key={k} type="button" role="tab" className={`bt-tab ${tab === k ? "active" : ""}`} onClick={() => setTab(k)} data-testid={`my-bordir-tab-${k}`}>
          {label} <span className="bt-cap">{n}</span></button>))}</nav>
      {tab === "aktif" && (d.tasks.length ? d.tasks.map(t => <ActiveTask key={t.id} t={t} reload={load}/>) : <span className="bt-muted">Tidak ada tugas aktif.</span>)}
      {tab === "serah" && <>
        {d.holdings.map(hd => <SendRow key={`${hd.order_number}-${hd.product_id}-${hd.bundle_id}`} hd={hd} operators={d.operators} reload={load}/>)}
        {d.outgoing_handovers.map(h => (
          <div key={h.id} className="bt-task" data-accent="queue" data-testid={`my-bordir-outgoing-${h.id}`}>
            SPK {h.order_number} · {h.bundle_label} · ke <b>{h.to_name}</b> {h.pcs_sent} pcs · <span className="bt-warn">{HO_ST[h.status]}</span> <WorkLeft h={h}/></div>))}
        {!nHo && <span className="bt-muted">Tidak ada barang yang perlu diserahkan.</span>}
      </>}
      {tab === "selesai" && d.done_tasks.map(t => <div key={t.id} className="bt-task" data-accent="ready" data-testid={`my-bordir-done-${t.id}`}><TaskLine t={t} showOrder/></div>)}
    </section>
  </>);
}
